import { PermissionCodes } from "@/shared/auth/permissions";
export type ShellKind = "auth" | "ops" | "field" | "exe" | "print";

export type ScreenMeta = {
  screenId: string;
  path: string;
  shell: ShellKind;
  label: string;
  module: string;
  /** BE permission codes (dot form). Empty = authenticated only. */
  permissions: string[];
  /** Wave that owns implementation */
  wave: "W0" | "W1" | "W2" | "W3" | "W4" | "W5";
  menuGroup?: string;
};

/**
 * Production screen registry — Sprint 2 seed + chrome.
 * Canonical IDs from prototype-manifest / 13_* — do not invent.
 */
export const screenRegistry: ScreenMeta[] = [
  {
    screenId: "SCR-AUTH-LOGIN",
    path: "/auth/login",
    shell: "auth",
    label: "Đăng nhập",
    module: "auth",
    permissions: [],
    wave: "W1",
  },
  {
    screenId: "SCR-OPS-HOME",
    path: "/ops",
    shell: "ops",
    label: "Tổng quan",
    module: "sys",
    permissions: [],
    wave: "W0",
    menuGroup: "Tổng quan",
  },
  {
    screenId: "SCR-BI-PROD",
    path: "/ops/bi/production",
    shell: "ops",
    label: "Báo cáo sản xuất",
    module: "bi",
    permissions: [PermissionCodes.BiProdList],
    wave: "W5",
    menuGroup: "Báo cáo",
  },
  {
    screenId: "SCR-BI-QC",
    path: "/ops/bi/quality",
    shell: "ops",
    label: "Báo cáo chất lượng",
    module: "bi",
    permissions: [PermissionCodes.BiQcList],
    wave: "W5",
    menuGroup: "Báo cáo",
  },
  {
    screenId: "SCR-BI-WH",
    path: "/ops/bi/warehouse",
    shell: "ops",
    label: "Báo cáo kho",
    module: "bi",
    permissions: [PermissionCodes.BiWhList],
    wave: "W5",
    menuGroup: "Báo cáo",
  },
  {
    screenId: "SCR-FIELD-HOME",
    path: "/field/home",
    shell: "field",
    label: "Trang chủ",
    module: "sys",
    permissions: [],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-MES-TODAY",
    path: "/field/today",
    shell: "field",
    label: "Hàng đợi",
    module: "mes",
    permissions: [PermissionCodes.MesBatchList],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-WMS-FIELD",
    path: "/field/wms",
    shell: "field",
    label: "Kho hiện trường",
    module: "warehouse",
    permissions: [PermissionCodes.WmsBalanceList, PermissionCodes.WmsTransactionList, PermissionCodes.WmsCountList, PermissionCodes.WmsExpiryList],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-WMS-FIELD-STOCK",
    path: "/field/wms/stock",
    shell: "field",
    label: "Field tồn kho",
    module: "warehouse",
    permissions: [PermissionCodes.WmsBalanceList],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-WMS-FIELD-EXPIRY",
    path: "/field/wms/expiry",
    shell: "field",
    label: "Field hạn dùng",
    module: "warehouse",
    permissions: [PermissionCodes.WmsExpiryList],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-WMS-FIELD-MOVE",
    path: "/field/wms/move",
    shell: "field",
    label: "Field chuyển vị trí",
    module: "warehouse",
    permissions: [PermissionCodes.WmsTransactionList, PermissionCodes.WmsTransactionManage, PermissionCodes.WmsTxnPost],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-WMS-FIELD-COUNT",
    path: "/field/wms/count",
    shell: "field",
    label: "Field kiểm kê",
    module: "warehouse",
    permissions: [PermissionCodes.WmsCountList, PermissionCodes.WmsCountManage],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-FIELD-PUR-PR",
    path: "/field/pur/requisitions",
    shell: "field",
    label: "Đề nghị mua hàng",
    module: "purchasing",
    permissions: [PermissionCodes.PurchaseRequisitionList, PermissionCodes.PurchaseRequest],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-MES-SCAN",
    path: "/field/scan",
    shell: "field",
    label: "Quét",
    module: "mes",
    permissions: [PermissionCodes.MesBatchList, PermissionCodes.MesOperationExecute],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-FIELD-BATCHES",
    path: "/field/batches",
    shell: "field",
    label: "Mẻ sản xuất",
    module: "mes",
    permissions: [PermissionCodes.MesBatchRead],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-FIELD-BATCH-DET",
    path: "/field/batches/:batchCode",
    shell: "field",
    label: "Chi tiết mẻ",
    module: "mes",
    permissions: [PermissionCodes.MesBatchRead],
    wave: "W5",
  },
  {
    screenId: "SCR-MES-CIP",
    path: "/field/cip",
    shell: "field",
    label: "Vệ sinh chuyền",
    module: "mes",
    permissions: [PermissionCodes.MesCipList, PermissionCodes.MesCipManage],
    wave: "W5",
    menuGroup: "Field",
  },
  {
    screenId: "SCR-MES-HANDOVER",
    path: "/field/handover",
    shell: "field",
    label: "Bàn giao",
    module: "mes",
    permissions: [PermissionCodes.MesHandoffList, PermissionCodes.MesHandoffManage],
    wave: "W5",
  },
  {
    screenId: "SCR-MES-REWORK",
    path: "/field/rework",
    shell: "field",
    label: "Gia công lại",
    module: "mes",
    permissions: [PermissionCodes.MesReworkList, PermissionCodes.MesReworkManage],
    wave: "W5",
  },
  {
    screenId: "SCR-MES-USAGE",
    path: "/field/usage",
    shell: "field",
    label: "Hao hụt / Usage",
    module: "mes",
    permissions: [PermissionCodes.MesLossList],
    wave: "W5",
  },
  {
    screenId: "SCR-MES-EXEC",
    path: "/field/exec/:operationId",
    shell: "field",
    label: "Thực thi công đoạn",
    module: "mes",
    permissions: [PermissionCodes.MesBatchList, PermissionCodes.MesOperationExecute],
    wave: "W5",
  },
  {
    screenId: "SCR-QMS-IPQC",
    path: "/field/ipqc/:operationId",
    shell: "field",
    label: "IPQC sàng lọc",
    module: "mes",
    permissions: [PermissionCodes.QmsInspectionList, PermissionCodes.QmsInspectionExecute],
    wave: "W5",
  },
  {
    screenId: "SCR-DOC-FORM-INST",
    path: "/field/forms/:instanceId",
    shell: "field",
    label: "Biểu mẫu công đoạn",
    module: "docs",
    permissions: [PermissionCodes.DocInstanceList, PermissionCodes.DocInstanceManage],
    wave: "W5",
  },
  {
    screenId: "SCR-DOC-MASTER",
    path: "/ops/doc/masters",
    shell: "ops",
    label: "Mẫu phiếu",
    module: "docs",
    permissions: [PermissionCodes.DocMasterList, PermissionCodes.DocMasterManage],
    wave: "W5",
    menuGroup: "Hồ sơ",
  },
  {
    screenId: "SCR-DOC-MASTER-DET",
    path: "/ops/doc/masters/:docCode",
    shell: "ops",
    label: "Chi tiết mẫu phiếu",
    module: "docs",
    permissions: [PermissionCodes.DocMasterList, PermissionCodes.DocMasterManage],
    wave: "W5",
  },
  {
    screenId: "SCR-DOC-FORM-WYSIWYG",
    path: "/ops/doc/masters/:docCode/design",
    shell: "ops",
    label: "Thiết kế mẫu in",
    module: "docs",
    permissions: [PermissionCodes.DocMasterList, PermissionCodes.DocMasterManage],
    wave: "W5",
  },
  {
    screenId: "SCR-DOC-FORM-PREVIEW",
    path: "/ops/doc/masters/:docCode",
    shell: "ops",
    label: "Xem trước Field/In",
    module: "docs",
    permissions: [PermissionCodes.DocMasterList],
    wave: "W5",
  },
  {
    screenId: "SCR-DOC-FORM-INST-OPS",
    path: "/ops/doc/instances",
    shell: "ops",
    label: "Phiếu đã điền",
    module: "docs",
    permissions: [PermissionCodes.DocInstanceList, PermissionCodes.DocInstanceManage],
    wave: "W5",
    menuGroup: "Hồ sơ",
  },
  {
    screenId: "SCR-DOC-SAVED",
    path: "/ops/doc/documents",
    shell: "ops",
    label: "Tài liệu",
    module: "docs",
    permissions: [PermissionCodes.DocAttachmentList, PermissionCodes.DocAttachmentManage],
    wave: "W5",
    menuGroup: "Hồ sơ",
  },
  {
    screenId: "SCR-DOC-SAVED-DET",
    path: "/ops/doc/documents/:attachmentId",
    shell: "ops",
    label: "Chi tiết tài liệu",
    module: "docs",
    permissions: [PermissionCodes.DocAttachmentList, PermissionCodes.DocAttachmentManage],
    wave: "W5",
  },
  {
    screenId: "SCR-MES-EBR",
    path: "/ops/mes/ebr/:batchCode",
    shell: "ops",
    label: "Hồ sơ mẻ",
    module: "mes",
    permissions: [PermissionCodes.MesBatchList, PermissionCodes.MesEbrClose],
    wave: "W5",
  },
  {
    screenId: "SCR-PRT-DOC",
    path: "/print/:docCode/:entityKey",
    shell: "print",
    label: "In chứng từ",
    module: "print",
    permissions: [
      PermissionCodes.WmsTransactionList,
      PermissionCodes.DocMasterList,
      PermissionCodes.DocInstanceList,
      PermissionCodes.SalesOrderList,
      PermissionCodes.PurchaseOrderList,
      PermissionCodes.PurchaseRequisitionList,
      PermissionCodes.QmsInspectionExecute,
    ],
    wave: "W5",
  },
  {
    screenId: "SCR-PRT-GR",
    path: "/print/gr/:txnNo",
    shell: "print",
    label: "In phiếu nhập (alias)",
    module: "print",
    permissions: [PermissionCodes.WmsTransactionList, PermissionCodes.WmsTxnPost],
    wave: "W5",
  },
  {
    screenId: "SCR-PRT-02VT",
    path: "/print/02-vt/:txnNo",
    shell: "print",
    label: "In phiếu 02-VT (alias)",
    module: "print",
    permissions: [PermissionCodes.WmsTransactionList],
    wave: "W5",
  },
  {
    screenId: "SCR-PRT-FORM",
    path: "/print/form/:instanceId",
    shell: "print",
    label: "In biểu mẫu",
    module: "print",
    permissions: [PermissionCodes.DocMasterList, PermissionCodes.DocInstanceList],
    wave: "W5",
  },
  {
    screenId: "SCR-PRT-EBR",
    path: "/print/ebr/:batchCode",
    shell: "print",
    label: "In hồ sơ mẻ",
    module: "print",
    permissions: [PermissionCodes.DocInstanceList, PermissionCodes.MesBatchList],
    wave: "W5",
  },
  {
    screenId: "SCR-MES-MY-ACTIVITY",
    path: "/field/activity",
    shell: "field",
    label: "Việc tôi đã làm",
    module: "mes",
    permissions: [PermissionCodes.MesBatchList],
    wave: "W5",
  },
  {
    screenId: "SCR-SYS-ACCOUNT",
    path: "/field/me",
    shell: "field",
    label: "Tài khoản",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-SYS-PROFILE",
    path: "/field/profile",
    shell: "field",
    label: "Hồ sơ",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-SYS-NOTIFY",
    path: "/field/notifications",
    shell: "field",
    label: "Thông báo",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-SYS-SETTINGS",
    path: "/field/settings",
    shell: "field",
    label: "Cài đặt ứng dụng",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-SYS-SYNC",
    path: "/field/sync",
    shell: "field",
    label: "Đồng bộ",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-EXE-HOME",
    path: "/exe",
    shell: "exe",
    label: "Ban GĐ",
    module: "executive",
    permissions: [PermissionCodes.ExeKpiRead],
    wave: "W0",
    menuGroup: "EXE",
  },
  {
    screenId: "SCR-EXE-APPROVALS",
    path: "/exe/approvals",
    shell: "exe",
    label: "Phê duyệt EXE",
    module: "executive",
    permissions: [PermissionCodes.ExeApprovalList, PermissionCodes.ExeApprovalAct],
    wave: "W5",
    menuGroup: "EXE",
  },
  {
    screenId: "SCR-EXE-APPROVAL-DETAIL",
    path: "/exe/approvals/:id",
    shell: "exe",
    label: "Chi tiết duyệt",
    module: "executive",
    permissions: [PermissionCodes.ExeApprovalAct],
    wave: "W5",
  },
  {
    screenId: "SCR-EXE-ALERTS",
    path: "/exe/alerts",
    shell: "exe",
    label: "Cảnh báo",
    module: "executive",
    permissions: [PermissionCodes.ExeAlertList],
    wave: "W5",
    menuGroup: "EXE",
  },
  {
    screenId: "SCR-EXE-REPORTS",
    path: "/exe/reports",
    shell: "exe",
    label: "Báo cáo",
    module: "executive",
    permissions: [PermissionCodes.ExeReportList],
    wave: "W5",
    menuGroup: "EXE",
  },
  {
    screenId: "SCR-EXE-ACCOUNT",
    path: "/exe/me",
    shell: "exe",
    label: "Tài khoản",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-EXE-PROFILE",
    path: "/exe/profile",
    shell: "exe",
    label: "Hồ sơ",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-EXE-NOTIFY",
    path: "/exe/notifications",
    shell: "exe",
    label: "Thông báo",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-EXE-SETTINGS",
    path: "/exe/settings",
    shell: "exe",
    label: "Cài đặt",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-EXE-REF",
    path: "/exe/ref/:kind",
    shell: "exe",
    label: "Chi tiết rủi ro",
    module: "executive",
    permissions: [PermissionCodes.ExeKpiRead],
    wave: "W5",
  },
  {
    screenId: "SCR-PRINT-HOME",
    path: "/print",
    shell: "print",
    label: "In ấn",
    module: "sys",
    permissions: [],
    wave: "W0",
  },
  {
    screenId: "SCR-UI-GALLERY",
    path: "/ops/_ui",
    shell: "ops",
    label: "UI Gallery",
    module: "sys",
    permissions: [],
    wave: "W2",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-ADMIN-HUB",
    path: "/ops/sys/admin",
    shell: "ops",
    label: "Quản trị",
    module: "sys",
    permissions: [],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-ORG",
    path: "/ops/sys/org",
    shell: "ops",
    label: "Tổ chức / Nhà máy",
    module: "sys",
    permissions: [PermissionCodes.SysPlantList, PermissionCodes.SysPlantManage],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-ORG-UNIT",
    path: "/ops/sys/organization",
    shell: "ops",
    label: "Pháp nhân & in",
    module: "sys",
    permissions: [PermissionCodes.SysOrgList, PermissionCodes.SysOrgManage],
    wave: "W5",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-USER",
    path: "/ops/sys/users",
    shell: "ops",
    label: "Người dùng",
    module: "sys",
    permissions: [PermissionCodes.SysUserList, PermissionCodes.SysUserManage],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-ROLE",
    path: "/ops/sys/roles",
    shell: "ops",
    label: "Vai trò",
    module: "sys",
    permissions: [PermissionCodes.SysRoleList, PermissionCodes.SysRoleManage],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-AUDIT",
    path: "/ops/sys/audit",
    shell: "ops",
    label: "Audit",
    module: "sys",
    permissions: [PermissionCodes.SysAuditRead],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-INT-EMAIL-LOG",
    path: "/ops/sys/email-logs",
    shell: "ops",
    label: "Nhật ký email",
    module: "sys",
    permissions: [PermissionCodes.SysEmailLogList],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-SHIFT",
    path: "/ops/sys/shifts",
    shell: "ops",
    label: "Ca làm việc",
    module: "sys",
    permissions: [PermissionCodes.SysShiftList, PermissionCodes.SysShiftManage],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-MOBILE-APP",
    path: "/ops/sys/mobile-app-releases",
    shell: "ops",
    label: "Phiên bản app",
    module: "sys",
    permissions: [
      PermissionCodes.SysMobileAppReleaseList,
      PermissionCodes.SysMobileAppReleaseManage,
    ],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-CATEGORY",
    path: "/ops/sys/categories",
    shell: "ops",
    label: "Danh mục",
    module: "sys",
    permissions: [PermissionCodes.SysCategoryList, PermissionCodes.SysCategoryManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-SYS-MFA",
    path: "/ops/sys/mfa",
    shell: "ops",
    label: "MFA · EXE",
    module: "sys",
    permissions: [PermissionCodes.SysMfaList, PermissionCodes.SysMfaManage],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-NUMBERING",
    path: "/ops/sys/numbering",
    shell: "ops",
    label: "Đánh số chứng từ",
    module: "sys",
    permissions: [PermissionCodes.SysNumberingList, PermissionCodes.SysNumberingManage],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-DEFAULTS",
    path: "/ops/sys/defaults",
    shell: "ops",
    label: "Giá trị mặc định",
    module: "sys",
    permissions: [PermissionCodes.SysSettingList, PermissionCodes.SysSettingManage],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-CONFIG-HUB",
    path: "/ops/sys/settings",
    shell: "ops",
    label: "Cấu hình hệ thống",
    module: "sys",
    permissions: [],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-STATUS-COLORS",
    path: "/ops/sys/status-colors",
    shell: "ops",
    label: "Màu sắc trạng thái & KPI",
    module: "sys",
    permissions: [],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-NOTIFY-CATALOG",
    path: "/ops/sys/notify-events",
    shell: "ops",
    label: "Thông báo · Sự kiện hệ thống",
    module: "sys",
    permissions: [PermissionCodes.SysNotifyManage],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-NAV-MENU",
    path: "/ops/sys/nav-menu",
    shell: "ops",
    label: "Điều hướng · Menu",
    module: "sys",
    permissions: [PermissionCodes.SysNavManage],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-APPROVAL-POLICIES",
    path: "/ops/sys/approval-policies",
    shell: "ops",
    label: "Phê duyệt · Chính sách",
    module: "approvals",
    permissions: [PermissionCodes.SysApprovalPolicyList, PermissionCodes.SysApprovalPolicyManage],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-PROFILE-OPS",
    path: "/ops/sys/profile",
    shell: "ops",
    label: "Hồ sơ tài khoản",
    module: "sys",
    permissions: [],
    wave: "W5",
  },
  {
    screenId: "SCR-SYS-NOTIFICATIONS",
    path: "/ops/sys/notifications",
    shell: "ops",
    label: "Thông báo của tôi",
    module: "sys",
    permissions: [],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-NOTIFY-PREF",
    path: "/ops/sys/notify-prefs",
    shell: "ops",
    label: "Cài đặt thông báo",
    module: "sys",
    permissions: [],
    wave: "W4",
    menuGroup: "Cấu hình hệ thống",
  },
  {
    screenId: "SCR-SYS-APPROVALS",
    path: "/ops/sys/approvals",
    shell: "ops",
    label: "Hộp thư duyệt",
    module: "approvals",
    permissions: [PermissionCodes.ApprovalAct],
    wave: "W4",
    menuGroup: "Công việc",
  },
  {
    screenId: "SCR-MDM-ITEM",
    path: "/ops/mdm/items",
    shell: "ops",
    label: "Hàng hóa",
    module: "mdm",
    permissions: [PermissionCodes.MdmItemRead],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-ITEM-CATEGORY",
    path: "/ops/mdm/item-categories",
    shell: "ops",
    label: "Loại hàng",
    module: "mdm",
    permissions: [PermissionCodes.MdmItemRead],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-UOM",
    path: "/ops/mdm/uoms",
    shell: "ops",
    label: "Đơn vị tính",
    module: "mdm",
    permissions: [PermissionCodes.MdmUomList, PermissionCodes.MdmUomManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-WH",
    path: "/ops/mdm/warehouses",
    shell: "ops",
    label: "Kho",
    module: "mdm",
    permissions: [PermissionCodes.MdmWarehouseList, PermissionCodes.MdmWarehouseManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-LOC",
    path: "/ops/mdm/locations",
    shell: "ops",
    label: "Vị trí kho",
    module: "mdm",
    permissions: [PermissionCodes.MdmLocationList, PermissionCodes.MdmLocationManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-EQUIP",
    path: "/ops/mdm/equipment",
    shell: "ops",
    label: "Thiết bị",
    module: "mdm",
    permissions: [PermissionCodes.MdmEquipmentList, PermissionCodes.MdmEquipmentManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-EQUIP-TYPE",
    path: "/ops/mdm/equipment-types",
    shell: "ops",
    label: "Loại thiết bị",
    module: "mdm",
    permissions: [PermissionCodes.MdmEquipmentList, PermissionCodes.MdmEquipmentManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-DEFECT",
    path: "/ops/mdm/defects",
    shell: "ops",
    label: "Mã lỗi",
    module: "mdm",
    permissions: [PermissionCodes.MdmDefectList, PermissionCodes.MdmDefectManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-REASON",
    path: "/ops/mdm/reasons",
    shell: "ops",
    label: "Mã lý do",
    module: "mdm",
    permissions: [PermissionCodes.MdmReasonList, PermissionCodes.MdmReasonManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-LOSS-TYPE",
    path: "/ops/mdm/loss-types",
    shell: "ops",
    label: "Loại hao hụt",
    module: "mdm",
    permissions: [PermissionCodes.MdmLossTypeList, PermissionCodes.MdmLossTypeManage],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-MDM-PARTNER-SEGMENT",
    path: "/ops/mdm/partner-segments",
    shell: "ops",
    label: "Phân khúc đối tác",
    module: "mdm",
    permissions: [
      PermissionCodes.MdmPartnerSegmentList,
      PermissionCodes.MdmPartnerSegmentManage,
    ],
    wave: "W4",
    menuGroup: "Danh mục",
  },
  {
    screenId: "SCR-SAL-CUSTOMER",
    path: "/ops/sal/customers",
    shell: "ops",
    label: "Khách hàng",
    module: "partners",
    permissions: [PermissionCodes.SalesManage],
    wave: "W4",
    menuGroup: "Bán hàng",
  },
  {
    screenId: "SCR-SAL-SO",
    path: "/ops/sal/orders",
    shell: "ops",
    label: "Đơn bán hàng",
    module: "sales",
    permissions: [PermissionCodes.SalesOrderList, PermissionCodes.SalesOrderManage],
    wave: "W5",
    menuGroup: "Bán hàng",
  },
  {
    screenId: "SCR-FIN-RECEIPT",
    path: "/ops/fin/receipts",
    shell: "ops",
    label: "Phiếu thu",
    module: "finance",
    permissions: [PermissionCodes.FinReceiptList, PermissionCodes.FinReceiptManage],
    wave: "W5",
    menuGroup: "Tài chính",
  },
  {
    screenId: "SCR-FIN-PAYMENT",
    path: "/ops/fin/payments",
    shell: "ops",
    label: "Phiếu chi",
    module: "finance",
    permissions: [PermissionCodes.FinPaymentList, PermissionCodes.FinPaymentManage],
    wave: "W5",
    menuGroup: "Tài chính",
  },
  {
    screenId: "SCR-FIN-TX",
    path: "/ops/fin/transactions",
    shell: "ops",
    label: "Lịch sử giao dịch",
    module: "finance",
    permissions: [PermissionCodes.FinTransactionList],
    wave: "W5",
    menuGroup: "Tài chính",
  },
  {
    screenId: "SCR-FIN-DEBT",
    path: "/ops/fin/debt-report",
    shell: "ops",
    label: "Báo cáo công nợ",
    module: "finance",
    permissions: [PermissionCodes.FinDebtReport],
    wave: "W5",
    menuGroup: "Tài chính",
  },
  {
    screenId: "SCR-SAL-SHIPMENT",
    path: "/ops/sal/shipments",
    shell: "ops",
    label: "Giao hàng",
    module: "sales",
    permissions: [
      PermissionCodes.SalesShipmentList,
      PermissionCodes.SalesShipmentManage,
    ],
    wave: "W5",
    menuGroup: "Giao hàng",
  },
  {
    screenId: "SCR-PUR-SUPPLIER",
    path: "/ops/pur/suppliers",
    shell: "ops",
    label: "Nhà cung cấp",
    module: "partners",
    permissions: [PermissionCodes.PurchaseManage],
    wave: "W4",
    menuGroup: "Mua hàng",
  },
  {
    screenId: "SCR-PUR-PR",
    path: "/ops/pur/requisitions",
    shell: "ops",
    label: "Đề nghị mua hàng",
    module: "purchasing",
    permissions: [PermissionCodes.PurchaseRequisitionList, PermissionCodes.PurchaseRequest],
    wave: "W5",
    menuGroup: "Mua hàng",
  },
  {
    screenId: "SCR-PUR-PO",
    path: "/ops/pur/orders",
    shell: "ops",
    label: "Đơn mua hàng",
    module: "purchasing",
    permissions: [PermissionCodes.PurchaseOrderList, PermissionCodes.PurchaseManage],
    wave: "W5",
    menuGroup: "Mua hàng",
  },
  {
    screenId: "SCR-PLN-DEMAND",
    path: "/ops/pln/demands",
    shell: "ops",
    label: "Nhu cầu",
    module: "planning",
    permissions: [PermissionCodes.PlnDemandList, PermissionCodes.PlnDemandManage],
    wave: "W5",
    menuGroup: "Kế hoạch",
  },
  {
    screenId: "SCR-PLN-ORDER",
    path: "/ops/pln/orders",
    shell: "ops",
    label: "Lệnh sản xuất",
    module: "planning",
    permissions: [PermissionCodes.PlnOrderList, PermissionCodes.PlnOrderManage],
    wave: "W5",
    menuGroup: "Kế hoạch",
  },
  {
    screenId: "SCR-PLN-RESERVE",
    path: "/ops/pln/reservations",
    shell: "ops",
    label: "Khoanh giữ NVL",
    module: "planning",
    permissions: [PermissionCodes.PlnReservationList, PermissionCodes.PlnReservationManage],
    wave: "W5",
    menuGroup: "Kế hoạch",
  },
  {
    screenId: "SCR-MES-BATCH",
    path: "/ops/mes/batches",
    shell: "ops",
    label: "Mẻ sản xuất",
    module: "mes",
    permissions: [PermissionCodes.MesBatchList, PermissionCodes.MesBatchManage],
    wave: "W5",
    menuGroup: "Sản xuất",
  },
  {
    screenId: "SCR-MES-ISSUE",
    path: "/ops/mes/material-issues",
    shell: "ops",
    label: "Cấp phát NVL",
    module: "mes",
    permissions: [PermissionCodes.PlnReservationList, PermissionCodes.PlnReservationManage],
    wave: "W5",
    menuGroup: "Sản xuất",
  },
  {
    screenId: "SCR-MES-HOLD",
    path: "/ops/mes/holds",
    shell: "ops",
    label: "HOLD",
    module: "mes",
    permissions: [PermissionCodes.MesHoldList, PermissionCodes.MesHoldManage, PermissionCodes.MesHoldRelease],
    wave: "W5",
    menuGroup: "Sản xuất",
  },
  {
    screenId: "SCR-MES-LOSS-LEDGER",
    path: "/ops/mes/losses",
    shell: "ops",
    label: "Sổ hao hụt",
    module: "mes",
    permissions: [PermissionCodes.MesLossList],
    wave: "W5",
    menuGroup: "Sản xuất",
  },
  {
    screenId: "SCR-ENG-OP-TEMPLATE",
    path: "/ops/eng/operation-templates",
    shell: "ops",
    label: "Danh mục công đoạn",
    module: "engineering",
    permissions: [PermissionCodes.EngOperationTemplateList, PermissionCodes.EngOperationTemplateManage],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-OP-TEMPLATE-DETAIL",
    path: "/ops/eng/operation-templates/:templateCode",
    shell: "ops",
    label: "Chi tiết công đoạn",
    module: "engineering",
    permissions: [PermissionCodes.EngOperationTemplateList, PermissionCodes.EngOperationTemplateManage],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-PACK-FORMAT",
    path: "/ops/eng/pack-formats",
    shell: "ops",
    label: "Danh mục quy cách",
    module: "engineering",
    permissions: [PermissionCodes.EngPackFormatList, PermissionCodes.EngPackFormatManage, PermissionCodes.EngBomApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-ONBOARD",
    path: "/ops/eng/onboarding",
    shell: "ops",
    label: "Hướng dẫn SP",
    module: "engineering",
    permissions: [PermissionCodes.EngBomList, PermissionCodes.EngTechnicalInfoList, PermissionCodes.EngRoutingList, PermissionCodes.EngSpecList, PermissionCodes.EngQcPlanList, PermissionCodes.EngOperationTemplateList],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-SYS-LINE",
    path: "/ops/sys/lines",
    shell: "ops",
    label: "Chuyền SX",
    module: "sys",
    permissions: [PermissionCodes.SysLineList, PermissionCodes.SysLineManage],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-SYS-AREA",
    path: "/ops/sys/areas",
    shell: "ops",
    label: "Khu vực SX",
    module: "sys",
    permissions: [PermissionCodes.SysAreaList, PermissionCodes.SysAreaManage],
    wave: "W4",
    menuGroup: "Quản trị",
  },
  {
    screenId: "SCR-ENG-PRODUCT-TYPE",
    path: "/ops/eng/product-types",
    shell: "ops",
    label: "Mẫu quy trình",
    module: "engineering",
    permissions: [PermissionCodes.EngProductTypeList, PermissionCodes.EngProductTypeManage],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-PRODUCT",
    path: "/ops/eng/products",
    shell: "ops",
    label: "Sản phẩm",
    module: "engineering",
    permissions: [PermissionCodes.EngProductList, PermissionCodes.EngProductManage, PermissionCodes.EngBomApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-TECHNICAL-INFO",
    path: "/ops/eng/technical-infos",
    shell: "ops",
    label: "Thông tin kỹ thuật SP",
    module: "engineering",
    permissions: [PermissionCodes.EngTechnicalInfoList, PermissionCodes.EngTechnicalInfoManage, PermissionCodes.EngTechnicalInfoApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-BOM",
    path: "/ops/eng/boms",
    shell: "ops",
    label: "BOM",
    module: "engineering",
    permissions: [PermissionCodes.EngBomList, PermissionCodes.EngBomManage, PermissionCodes.EngBomApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-ROUTING",
    path: "/ops/eng/routings",
    shell: "ops",
    label: "Routing",
    module: "engineering",
    permissions: [PermissionCodes.EngRoutingList, PermissionCodes.EngRoutingManage, PermissionCodes.EngRoutingApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-ROUTING-NEW",
    path: "/ops/eng/routings/new",
    shell: "ops",
    label: "Tạo Routing",
    module: "engineering",
    permissions: [PermissionCodes.EngRoutingManage],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-SPEC",
    path: "/ops/eng/specs",
    shell: "ops",
    label: "Tiêu chuẩn kỹ thuật",
    module: "engineering",
    permissions: [PermissionCodes.EngSpecList, PermissionCodes.EngSpecManage, PermissionCodes.EngSpecApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-QCPLAN",
    path: "/ops/eng/qc-plans",
    shell: "ops",
    label: "Mẫu QC",
    module: "engineering",
    permissions: [PermissionCodes.EngQcPlanList, PermissionCodes.EngQcPlanManage, PermissionCodes.EngQcPlanApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-ENG-RELEASE",
    path: "/ops/eng/releases",
    shell: "ops",
    label: "Phê duyệt kỹ thuật",
    module: "approvals",
    permissions: [PermissionCodes.ApprovalAct, PermissionCodes.EngBomApprove, PermissionCodes.EngTechnicalInfoApprove, PermissionCodes.EngRoutingApprove, PermissionCodes.EngSpecApprove, PermissionCodes.EngQcPlanApprove],
    wave: "W5",
    menuGroup: "Kỹ thuật",
  },
  {
    screenId: "SCR-WMS-GR",
    path: "/ops/wms/receipts",
    shell: "ops",
    label: "Phiếu nhập",
    module: "warehouse",
    permissions: [PermissionCodes.WmsTransactionList, PermissionCodes.WmsTransactionManage, PermissionCodes.WmsTxnPost, PermissionCodes.PurchaseManage],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-LOT",
    path: "/ops/wms/lots",
    shell: "ops",
    label: "Lô",
    module: "warehouse",
    permissions: [PermissionCodes.WmsLotList, PermissionCodes.WmsLotManage],
    wave: "W5",
    // Hidden from Ops menu (8.2); deep links / lot detail routes remain.
  },
  {
    screenId: "SCR-WMS-GI",
    path: "/ops/wms/issues",
    shell: "ops",
    label: "Phiếu xuất",
    module: "warehouse",
    permissions: [PermissionCodes.WmsTransactionList, PermissionCodes.WmsTransactionManage, PermissionCodes.WmsTxnPost],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-MOVE",
    path: "/ops/wms/moves",
    shell: "ops",
    label: "Chuyển vị trí",
    module: "warehouse",
    permissions: [PermissionCodes.WmsTransactionList, PermissionCodes.WmsTransactionManage, PermissionCodes.WmsTxnPost],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-STOCK",
    path: "/ops/wms/stock",
    shell: "ops",
    label: "Tồn kho",
    module: "warehouse",
    permissions: [PermissionCodes.WmsBalanceList],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-STOCK-REPORT",
    path: "/ops/wms/stock-report",
    shell: "ops",
    label: "Báo cáo tồn kho",
    module: "warehouse",
    permissions: [PermissionCodes.WmsBalanceList],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-RESERVATION",
    path: "/ops/wms/reservations",
    shell: "ops",
    label: "Khoanh giữ",
    module: "warehouse",
    permissions: [PermissionCodes.WmsReservationList, PermissionCodes.WmsReservationManage],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-EXPIRY",
    path: "/ops/wms/expiry",
    shell: "ops",
    label: "Hạn dùng",
    module: "warehouse",
    permissions: [PermissionCodes.WmsExpiryList, PermissionCodes.WmsExpiryManage],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-COUNT",
    path: "/ops/wms/counts",
    shell: "ops",
    label: "Kiểm kê",
    module: "warehouse",
    permissions: [PermissionCodes.WmsCountList, PermissionCodes.WmsCountManage, PermissionCodes.WmsStockManage],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-WMS-DISPOSAL",
    path: "/ops/wms/disposals",
    shell: "ops",
    label: "Tiêu hủy",
    module: "warehouse",
    permissions: [PermissionCodes.WmsDisposalList, PermissionCodes.WmsDisposalManage],
    wave: "W5",
    menuGroup: "Kho",
  },
  {
    screenId: "SCR-QMS-IQC",
    path: "/ops/qms/iqc",
    shell: "ops",
    label: "Kiểm tra đầu vào",
    module: "qms",
    permissions: [PermissionCodes.QmsIqcList, PermissionCodes.QmsInspectionExecute, PermissionCodes.QmsReleaseDecide],
    wave: "W5",
    menuGroup: "Chất lượng",
  },
  {
    screenId: "SCR-QMS-NCR",
    path: "/ops/qms/ncrs",
    shell: "ops",
    label: "Biên bản sự cố",
    module: "qms",
    permissions: [PermissionCodes.QmsNcrList, PermissionCodes.QmsNcrManage, PermissionCodes.QmsReleaseDecide],
    wave: "W5",
    menuGroup: "Chất lượng",
  },
  {
    screenId: "SCR-QMS-RELEASE",
    path: "/ops/qms/releases",
    shell: "ops",
    label: "Thẩm định xuất xưởng",
    module: "qms",
    permissions: [PermissionCodes.QmsReleaseList, PermissionCodes.QmsReleaseDecide],
    wave: "W5",
    menuGroup: "Chất lượng",
  },
  {
    screenId: "SCR-QMS-FQC",
    path: "/ops/qms/fqc",
    shell: "ops",
    label: "Kiểm tra thành phẩm",
    module: "qms",
    permissions: [PermissionCodes.QmsFqcList, PermissionCodes.QmsInspectionExecute, PermissionCodes.QmsReleaseDecide],
    wave: "W5",
    menuGroup: "Chất lượng",
  },
  {
    screenId: "SCR-QMS-DEVIATION",
    path: "/ops/qms/deviations",
    shell: "ops",
    label: "Sai lệch",
    module: "qms",
    permissions: [PermissionCodes.QmsDeviationList, PermissionCodes.QmsDeviationManage],
    wave: "W5",
    menuGroup: "Chất lượng",
  },
  {
    screenId: "SCR-TRC-SEARCH",
    path: "/ops/trc",
    shell: "ops",
    label: "Truy xuất",
    module: "trace",
    permissions: [PermissionCodes.TrcTraceList, PermissionCodes.TrcTraceManage],
    wave: "W5",
    menuGroup: "Chất lượng",
  },
  {
    screenId: "SCR-TRC-TREE",
    path: "/ops/trc/lots/:lotCode",
    shell: "ops",
    label: "Phả hệ lô",
    module: "trace",
    permissions: [PermissionCodes.WmsStockRead],
    wave: "W5",
  },
];

export function getScreenById(screenId: string): ScreenMeta | undefined {
  return screenRegistry.find((s) => s.screenId === screenId);
}

export function getScreenByPath(path: string): ScreenMeta | undefined {
  return screenRegistry.find((s) => s.path === path);
}
